import React, { useEffect, useState } from 'react';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Button,
  Chip,
  Divider,
  CircularProgress,
  Tabs,
  Tab,
  Paper,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import transcriptionService from '../services/transcriptionService';
import type { SessionDetail, ReportData } from '../services/transcriptionService';
import ReportPreviewPanel from './ReportPreviewPanel';

interface SessionDetailDrawerProps {
  sessionId: string | null;
  open: boolean;
  onClose: () => void;
}

const SessionDetailDrawer: React.FC<SessionDetailDrawerProps> = ({
  sessionId,
  open,
  onClose,
}) => {
  const navigate = useNavigate();
  const gradingEnabled = useSelector((state: { report: { config?: { gradingEnabled?: boolean } } }) => state.report.config?.gradingEnabled ?? false);
  const gradingMode = useSelector((state: { report: { config?: { gradingEnforcementMode?: string } } }) => state.report.config?.gradingEnforcementMode ?? 'warn');
  const [detail, setDetail] = useState<SessionDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState(0);

  useEffect(() => {
    if (!sessionId || !open) return;
    setLoading(true);
    setError(null);
    setTab(0);
    transcriptionService
      .getSessionDetail(sessionId)
      .then(setDetail)
      .catch((err) => {
        setError(err?.response?.data?.detail || 'Failed to load dictation session');
      })
      .finally(() => setLoading(false));
  }, [sessionId, open]);

  const handleReportGenerated = (report: ReportData) => {
    if (detail) {
      setDetail({ ...detail, report });
    }
  };

  const formatTime = (seconds: number): string => {
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width: { xs: '100%', sm: 600, md: 720 } } }}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', p: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Typography variant="h6" sx={{ flexGrow: 1 }}>
          Dictation Details
        </Typography>
        {sessionId && detail && (
          detail.report?.status === 'final' ? (
            <Button
              size="small"
              variant="outlined"
              startIcon={<VisibilityIcon />}
              onClick={() => {
                onClose();
                navigate(`/report/edit?session=${sessionId}&mode=view`);
              }}
              sx={{ mr: 1 }}
            >
              View Report
            </Button>
          ) : (
            <>
              <Button
                size="small"
                variant="outlined"
                startIcon={<EditIcon />}
                onClick={() => {
                  onClose();
                  navigate(`/transcribe?session=${sessionId}`);
                }}
                sx={{ mr: 1 }}
              >
                Open & Edit
              </Button>
              {detail.report && (
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<OpenInNewIcon />}
                  onClick={() => {
                    onClose();
                    navigate(`/report/edit?session=${sessionId}&mode=edit`);
                  }}
                  sx={{ mr: 1 }}
                >
                  Report Editor
                </Button>
              )}
            </>
          )
        )}
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </Box>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
          <CircularProgress />
        </Box>
      )}

      {error && (
        <Box sx={{ p: 3 }}>
          <Typography color="error">{error}</Typography>
        </Box>
      )}

      {!loading && !error && detail && (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
          {/* Session metadata */}
          <Box sx={{ px: 2, py: 1.5, display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center' }}>
            <Box>
              <Typography variant="caption" color="text.secondary">Date</Typography>
              <Typography variant="body2">
                {new Date(detail.created_at).toLocaleString()}
              </Typography>
            </Box>
            <Divider orientation="vertical" flexItem />
            <Box>
              <Typography variant="caption" color="text.secondary">Duration</Typography>
              <Typography variant="body2">{formatTime(detail.duration_seconds)}</Typography>
            </Box>
            <Chip
              label={detail.status}
              color={detail.status === 'completed' || detail.status === 'corrected' ? 'success' : 'primary'}
              size="small"
            />
          </Box>

          {/* Diagnosis chips */}
          {(detail.diagnosis_category || detail.pathology_type) && (
            <Box sx={{ px: 2, pb: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {detail.diagnosis_category && (
                <Chip label={detail.diagnosis_category} size="small" color="primary" variant="outlined" />
              )}
              {detail.pathology_type && (
                <Chip label={detail.pathology_type} size="small" color="secondary" variant="outlined" />
              )}
              {detail.classification_confidence != null && (
                <Chip
                  label={`Confidence: ${(detail.classification_confidence * 100).toFixed(0)}%`}
                  size="small"
                  variant="outlined"
                  color={detail.classification_confidence >= 0.6 ? 'success' : 'warning'}
                />
              )}
            </Box>
          )}

          <Divider />

          {/* Tabs */}
          <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ px: 2, minHeight: 42 }}>
            <Tab label="Dictation" sx={{ minHeight: 42,  }} />
            <Tab label="Report" sx={{ minHeight: 42,  }} />
          </Tabs>

          <Divider />

          {/* Tab content */}
          <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
            {tab === 0 && (
              <Box>
                {/* Corrected transcript */}
                {detail.corrected_text && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Reviewed Dictation
                    </Typography>
                    <Paper variant="outlined" sx={{ p: 2, backgroundColor: '#f0fdf4' }}>
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                        {detail.corrected_text}
                      </Typography>
                    </Paper>
                  </Box>
                )}

                {/* Raw transcript */}
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    {detail.corrected_text ? 'Dictation Capture' : 'Dictation'}
                  </Typography>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                      {detail.text || 'No dictation text available.'}
                    </Typography>
                  </Paper>
                </Box>
              </Box>
            )}

            {tab === 1 && (
              <ReportPreviewPanel
                report={detail.report}
                sessionId={detail.session_id}
                onReportGenerated={handleReportGenerated}
                gradingEnabled={gradingEnabled}
                gradingMode={gradingMode}
              />
            )}
          </Box>
        </Box>
      )}
    </Drawer>
  );
};

export default SessionDetailDrawer;
